import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';

const IconCard = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
  </svg>
);

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const FEATURES = [
  'AI-powered clip detection on all your streams',
  'Automatic YouTube Shorts posting',
  'Content calendar with smart scheduling',
  'Growth insights updated weekly',
  'YouTube analytics per clip',
  'Unlimited clips per month',
];

export default function Billing() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for success/cancelled params
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') === 'success') {
      window.history.replaceState({}, '', '/billing');
    }

    API.get('/api/billing/status').then(r => {
      setStatus(r.data);
      setLoading(false);
      // If already active/trialing and came from success redirect, go to dashboard
      const params = new URLSearchParams(window.location.search);
      if (params.get('billing') === 'success' && ['active','trialing'].includes(r.data?.status)) {
        navigate('/dashboard');
      }
    }).catch(() => setLoading(false));
  }, []);

  const startTrial = async () => {
    setActionLoading(true);
    try {
      const res = await API.post('/api/billing/checkout', { returnUrl: window.location.href });
      window.location.href = res.data.url;
    } catch (err) {
      console.error(err);
      setActionLoading(false);
    }
  };

  const manageSubscription = async () => {
    setActionLoading(true);
    try {
      const res = await API.post('/api/billing/portal', { returnUrl: window.location.href });
      window.location.href = res.data.url;
    } catch (err) {
      console.error(err);
      setActionLoading(false);
    }
  };

  const fmt = (date) => new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  const renderStatus = () => {
    if (!status || status.status === 'none') {
      return (
        <div style={{background:'var(--accent-dim)', border:'1px solid rgba(200,241,53,0.2)', borderRadius:12, padding:'20px 24px', marginBottom:24, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12}}>
          <div>
            <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:16}}>Start your free trial</div>
            <div style={{color:'var(--text-muted)', fontSize:13, marginTop:2}}>14 days free, then £19/month. Cancel anytime.</div>
          </div>
          <button className="btn btn-primary" style={{width:'auto'}} onClick={startTrial} disabled={actionLoading}>
            {actionLoading ? 'Loading...' : 'Start 14-day free trial →'}
          </button>
        </div>
      );
    }

    if (status.status === 'trialing') {
      return (
        <div style={{background:'var(--accent-dim)', border:'1px solid rgba(200,241,53,0.2)', borderRadius:12, padding:'20px 24px', marginBottom:24, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12}}>
          <div>
            <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:16, color:'var(--accent)'}}>
              Free trial — {status.trialDaysRemaining} day{status.trialDaysRemaining !== 1 ? 's' : ''} remaining
            </div>
            <div style={{color:'var(--text-muted)', fontSize:13, marginTop:2}}>
              Trial ends {fmt(status.trialEnd)}. You won't be charged until then.
            </div>
          </div>
          <button className="btn btn-ghost" style={{width:'auto', fontSize:13}} onClick={manageSubscription} disabled={actionLoading}>
            {actionLoading ? 'Loading...' : 'Manage subscription'}
          </button>
        </div>
      );
    }

    if (status.status === 'active') {
      return (
        <div style={{background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'20px 24px', marginBottom:24, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12}}>
          <div>
            <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:4}}>
              <span style={{background:'var(--accent-dim)', color:'var(--accent)', fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:4, textTransform:'uppercase', letterSpacing:'0.06em'}}>Active</span>
              <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:16}}>Clump Pro — £19/month</div>
            </div>
            <div style={{color:'var(--text-muted)', fontSize:13}}>
              Next billing date: {fmt(status.periodEnd)}
            </div>
          </div>
          <button className="btn btn-ghost" style={{width:'auto', fontSize:13}} onClick={manageSubscription} disabled={actionLoading}>
            {actionLoading ? 'Loading...' : 'Manage subscription'}
          </button>
        </div>
      );
    }

    if (status.status === 'cancelled' || status.status === 'canceled') {
      return (
        <div style={{background:'rgba(255,68,68,0.05)', border:'1px solid rgba(255,68,68,0.15)', borderRadius:12, padding:'20px 24px', marginBottom:24, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12}}>
          <div>
            <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:16, color:'#ff4444'}}>Subscription cancelled</div>
            <div style={{color:'var(--text-muted)', fontSize:13, marginTop:2}}>Resubscribe to continue using Clump.</div>
          </div>
          <button className="btn btn-primary" style={{width:'auto'}} onClick={startTrial} disabled={actionLoading}>
            {actionLoading ? 'Loading...' : 'Resubscribe — £19/month'}
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Billing</div>
        <div className="page-subtitle">Manage your Clump subscription</div>
      </div>

      {loading ? (
        <div style={{color:'var(--text-muted)', fontSize:14}}>Loading billing info...</div>
      ) : (
        <>
          {renderStatus()}

          {/* Plan details */}
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
            <div style={{background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:28}}>
              <div style={{fontFamily:'var(--font-display)', fontWeight:800, fontSize:22, marginBottom:4}}>Clump Pro</div>
              <div style={{display:'flex', alignItems:'baseline', gap:4, marginBottom:20}}>
                <span style={{fontFamily:'var(--font-display)', fontWeight:800, fontSize:36, color:'var(--accent)'}}>£19</span>
                <span style={{color:'var(--text-muted)', fontSize:14}}>/month</span>
              </div>
              <div style={{fontSize:12, color:'var(--text-muted)', marginBottom:16, padding:'8px 12px', background:'var(--accent-dim)', borderRadius:6, color:'var(--accent)', fontWeight:500}}>
                14-day free trial included
              </div>
              <div style={{display:'flex', flexDirection:'column', gap:10}}>
                {FEATURES.map((f, i) => (
                  <div key={i} style={{display:'flex', alignItems:'center', gap:10, fontSize:13}}>
                    <IconCheck />{f}
                  </div>
                ))}
              </div>
            </div>

            <div style={{background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:28}}>
              <div style={{fontFamily:'var(--font-display)', fontWeight:700, fontSize:16, marginBottom:16}}>Billing FAQ</div>
              {[
                { q: 'When will I be charged?', a: 'After your 14-day free trial ends. You\'ll get an email reminder 1 day before.' },
                { q: 'Can I cancel anytime?', a: 'Yes. Cancel before your trial ends and you won\'t be charged at all.' },
                { q: 'What happens if I cancel?', a: 'You keep access until the end of your billing period, then your account is paused.' },

              ].map((item, i) => (
                <div key={i} style={{marginBottom:16}}>
                  <div style={{fontSize:13, fontWeight:600, marginBottom:4}}>{item.q}</div>
                  <div style={{fontSize:12, color:'var(--text-muted)', lineHeight:1.6}}>{item.a}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      <div style={{textAlign:'center', marginTop:24, fontSize:13, color:'var(--text-muted)'}}>
        Already have an account? <a href="/login" style={{color:'var(--accent)', textDecoration:'none'}}>Sign in →</a>
      </div>
    </div>
  );
}
